import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export interface Tab {
  id: string
  type: 'conversation' | 'document'
  title: string
  sessionId?: string
  docSlug?: string
  createdAt: number
}

interface TabsState {
  tabs: Tab[]
  activeTabId: string | null
  addTab: (tab: Tab) => void
  removeTab: (tabId: string) => void
  setActive: (tabId: string) => void
  updateTabTitle: (tabId: string, title: string) => void
  /** Open a tab for an existing session. If a tab with this sessionId already exists, activate it. */
  openExistingSession: (sessionId: string, title: string) => void
}

/** Strip control characters (U+0000–U+001F except tab/newline, plus U+007F) */
function sanitizeTitle(title: string): string {
  // eslint-disable-next-line no-control-regex
  return title.replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '').trim()
}

export const useTabsStore = create<TabsState>()(
  persist(
    (set, get) => ({
      tabs: [],
      activeTabId: null,

      addTab: (tab) => {
        const existing = get().tabs.find(t => t.id === tab.id)
        if (existing) {
          set({ activeTabId: tab.id })
          return
        }
        const sanitized = { ...tab, title: sanitizeTitle(tab.title) }
        set(state => ({
          tabs: [...state.tabs, sanitized],
          activeTabId: sanitized.id,
        }))
      },

      removeTab: (tabId) => {
        const { tabs, activeTabId } = get()
        const idx = tabs.findIndex(t => t.id === tabId)
        const newTabs = tabs.filter(t => t.id !== tabId)
        let newActive = activeTabId

        if (activeTabId === tabId) {
          if (newTabs.length > 0) {
            newActive = newTabs[Math.min(idx, newTabs.length - 1)].id
          } else {
            newActive = null
          }
        }
        set({ tabs: newTabs, activeTabId: newActive })
      },

      setActive: (tabId) => set({ activeTabId: tabId }),

      updateTabTitle: (tabId, title) =>
        set(state => ({
          tabs: state.tabs.map(t => t.id === tabId ? { ...t, title: sanitizeTitle(title) } : t),
        })),

      openExistingSession: (sessionId, title) => {
        const existing = get().tabs.find(t => t.sessionId === sessionId)
        if (existing) {
          set({ activeTabId: existing.id })
          return
        }
        const tab: Tab = {
          id: sessionId,
          type: 'conversation',
          title: sanitizeTitle(title),
          sessionId,
          createdAt: Date.now(),
        }
        set(state => ({
          tabs: [...state.tabs, tab],
          activeTabId: tab.id,
        }))
      },
    }),
    {
      name: 'mb-tabs',
      partialize: (state) => ({ tabs: state.tabs, activeTabId: state.activeTabId }),
      storage: {
        getItem: (name) => {
          try {
            const raw = localStorage.getItem(name)
            if (raw) JSON.parse(raw) // validate only — zustand/persist does its own parse
            return raw
          } catch {
            localStorage.removeItem(name)
            return null
          }
        },
        setItem: (name, value) => localStorage.setItem(name, value),
        removeItem: (name) => localStorage.removeItem(name),
      },
    },
  ),
)
